import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronLeft, FiChevronRight, FiCheck, FiRotateCcw, FiCalendar, FiBookOpen } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import InteractivePassage from "../bible/InteractivePassage";
import LoginForm from "../LoginForm";
import { useModal } from "../../context/Modal";
import ReadingPlanWeek from "./ReadingPlanWeek";
import ReadingPlanUnavailable from "./ReadingPlanUnavailable";
import { fetchUserSettings } from "../../store/settingsSlice";
import { versionForLanguage } from "../../utils/bibleVersions";

const VERSION_OPTIONS = ["ESV", "KRV", "NIV", "NLT", "SAE", "ABB"];

const NEU_RAISED_LG =
  "shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]";
const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";
const NEU_INSET_SM =
  "shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";

const clientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_e) {
    return null;
  }
};

/**
 * Renders one plan-day segment. If the segment spans more than one chapter,
 * paginate it chapter-by-chapter with simple arrow navigation so a "Amos 4-6"
 * assignment becomes three swipeable chapter pages.
 */
const SegmentSection = ({ segment, index, total, version, onVersionChange, user, onRequireLogin, language }) => {
  const chapters = useMemo(() => {
    const seen = [];
    for (const v of segment.verses || []) {
      if (typeof v.chapter === "number" && !seen.includes(v.chapter)) seen.push(v.chapter);
    }
    return seen.sort((a, b) => a - b);
  }, [segment]);

  const [chapterIdx, setChapterIdx] = useState(0);
  useEffect(() => { setChapterIdx(0); }, [segment.id]);

  const multiChapter = chapters.length > 1;
  const currentChapter = chapters[chapterIdx];
  const versesToRender = useMemo(() => {
    if (!multiChapter) return segment.verses || [];
    return (segment.verses || []).filter((v) => v.chapter === currentChapter);
  }, [segment, currentChapter, multiChapter]);

  const bookName = (segment.reference_en || segment.reference_ko || "").split(/\s+\d/)[0].trim();
  const chapterLabel = multiChapter && bookName ? `${bookName} ${currentChapter}` : null;

  const prev = () => setChapterIdx((i) => Math.max(0, i - 1));
  const next = () => setChapterIdx((i) => Math.min(chapters.length - 1, i + 1));

  return (
    <section
      className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD} flex flex-col gap-space-md`}
    >
      <div className="flex items-start justify-between gap-space-md">
        <div className="flex flex-col gap-space-xs min-w-0">
          <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
            Passage {index + 1} of {total}
          </span>
          <h2 className="font-noto-serif text-headline-md text-neu-primary">
            {language === "ko"
              ? segment.reference_ko || segment.reference_en
              : segment.reference_en || segment.reference_ko}
          </h2>
        </div>
        {/* Bible version selector — top-right of the passage card. Rendered
            only on the first segment so multi-passage days don't repeat it. */}
        {index === 0 && onVersionChange && (
          <label
            className={`shrink-0 inline-flex items-center gap-2 h-11 pl-4 pr-2 rounded-full bg-canvas-base ${NEU_INSET_SM} text-label-md text-neu-primary`}
            aria-label="Bible version"
          >
            <FiBookOpen aria-hidden="true" className="text-accent-cta" />
            <span className="font-semibold hidden sm:inline">Bible</span>
            <select
              value={version}
              onChange={(e) => onVersionChange(e.target.value)}
              className="h-9 rounded-full bg-white px-3 pr-6 text-sm text-neu-primary font-semibold border-none outline-none focus:ring-2 focus:ring-[color:var(--accent-cta)]/30"
              aria-label="Choose Bible version"
            >
              {VERSION_OPTIONS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {multiChapter && (
        <div className="flex items-center justify-between gap-space-sm">
          <button
            type="button"
            onClick={prev}
            disabled={chapterIdx === 0}
            aria-label="Previous chapter"
            className={`w-10 h-10 rounded-full flex items-center justify-center bg-canvas-base text-neu-primary ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <FiChevronLeft />
          </button>
          <div className="flex flex-col items-center">
            <span className="font-noto-serif text-headline-sm text-neu-primary">
              {chapterLabel}
            </span>
            <span className="text-label-sm text-slate-muted">
              Chapter {chapterIdx + 1} of {chapters.length}
            </span>
          </div>
          <button
            type="button"
            onClick={next}
            disabled={chapterIdx === chapters.length - 1}
            aria-label="Next chapter"
            className={`w-10 h-10 rounded-full flex items-center justify-center bg-canvas-base text-neu-primary ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <FiChevronRight />
          </button>
        </div>
      )}

      <InteractivePassage
        verses={versesToRender}
        version={version}
        user={user}
        actions={["memo", "highlight", "copy"]}
        onRequireLogin={onRequireLogin}
      />

      {multiChapter && chapters.length > 1 && (
        <div className="flex items-center justify-between gap-space-sm pt-space-xs">
          <button
            type="button"
            onClick={prev}
            disabled={chapterIdx === 0}
            className={`inline-flex items-center gap-1 h-10 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <FiChevronLeft />
            <span>Previous chapter</span>
          </button>
          <button
            type="button"
            onClick={next}
            disabled={chapterIdx === chapters.length - 1}
            className={`inline-flex items-center gap-1 h-10 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <span>Next chapter</span>
            <FiChevronRight />
          </button>
        </div>
      )}
    </section>
  );
};

const PlanModeReader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { planSlug, date } = useParams();
  const user = useSelector((state) => state.session.user);
  const { defaultVersion, isLoaded: settingsLoaded } = useSelector((state) => state.settings);
  const { setModalContent } = useModal();
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith("ko") ? "ko" : "en";

  // Bible version: follows the current nav language, preferring the user's
  // saved default when it belongs to that language. A manual dropdown pick
  // sets userOverride and pauses the sync until the language changes.
  const [version, setVersion] = useState(() => versionForLanguage(language, defaultVersion));
  const [userOverride, setUserOverride] = useState(false);

  useEffect(() => {
    if (user && !settingsLoaded) dispatch(fetchUserSettings());
  }, [dispatch, user, settingsLoaded]);

  useEffect(() => {
    setUserOverride(false);
  }, [language]);

  useEffect(() => {
    if (userOverride) return;
    const next = versionForLanguage(language, defaultVersion);
    setVersion((current) => (current === next ? current : next));
  }, [defaultVersion, language, userOverride]);

  const changeVersion = (nextVersion) => {
    setUserOverride(true);
    setVersion(nextVersion);
  };

  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("bibleMemosTheme") || "powder"; } catch (_e) { return "powder"; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const [planMeta, setPlanMeta] = useState(null);
  const [progress, setProgress] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Weekly plan sidebar (desktop) / drawer (mobile).
  const [weekDays, setWeekDays] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [weekError, setWeekError] = useState(null);
  const [weekRange, setWeekRange] = useState({ start: null, end: null });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [today, setToday] = useState(null);

  const tz = useMemo(clientTimezone, []);
  const commonHeaders = useMemo(
    () => (tz ? { "X-Client-Timezone": tz } : {}),
    [tz]
  );

  const openLogin = useCallback(() => setModalContent(<LoginForm />), [setModalContent]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planRes, dayRes, progressRes] = await Promise.all([
        fetch(`/api/reading-plans/${planSlug}`, { headers: commonHeaders, credentials: "include" }),
        fetch(`/api/reading-plans/${planSlug}/days/${date}?version=${encodeURIComponent(version)}`, { headers: commonHeaders, credentials: "include" }),
        fetch(`/api/reading-plans/${planSlug}/progress`, { headers: commonHeaders, credentials: "include" }),
      ]);
      if (!dayRes.ok) {
        const body = await dayRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load reading");
      }
      const [plan, day, prog] = await Promise.all([
        planRes.ok ? planRes.json() : null,
        dayRes.json(),
        progressRes.ok ? progressRes.json() : null,
      ]);
      setPlanMeta(plan);
      setDayDetail(day);
      setProgress(prog);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [planSlug, date, version, commonHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Fetch today (server-authoritative) once — used for the "Return to Today"
  // shortcut and the today badge in the week sidebar.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/reading-plans/${planSlug}/today`, {
      headers: commonHeaders,
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => { if (!cancelled) setToday(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [planSlug, commonHeaders]);

  // Compute the Sun-Sat window around the selected date, then fetch its days.
  useEffect(() => {
    if (!date) return;
    const d = new Date(`${date}T00:00:00`);
    const dow = d.getDay(); // 0 = Sunday
    const sunday = new Date(d);
    sunday.setDate(d.getDate() - dow);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    const iso = (dt) =>
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const start = iso(sunday);
    const end = iso(saturday);
    setWeekRange({ start, end });

    let cancelled = false;
    setWeekLoading(true);
    setWeekError(null);
    fetch(`/api/reading-plans/${planSlug}/days?start=${start}&end=${end}`, {
      headers: commonHeaders,
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || "Could not load week");
        }
        return r.json();
      })
      .then((rows) => { if (!cancelled) setWeekDays(rows); })
      .catch((err) => { if (!cancelled) setWeekError(err.message); })
      .finally(() => { if (!cancelled) setWeekLoading(false); });
    return () => { cancelled = true; };
  }, [date, planSlug, commonHeaders]);

  // Close the mobile drawer when the route (date) changes.
  useEffect(() => { setDrawerOpen(false); }, [date]);

  // Close the drawer on Escape.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const daysByDate = useMemo(() => {
    const map = new Map();
    for (const day of weekDays) {
      if (!map.has(day.date)) map.set(day.date, []);
      map.get(day.date).push(day);
    }
    return map;
  }, [weekDays]);

  const completedIds = useMemo(
    () => new Set(progress?.completed_day_ids || []),
    [progress]
  );

  const allCompleted = Boolean(dayDetail?.completed);

  const toggleComplete = async () => {
    if (!user) { openLogin(); return; }
    if (!dayDetail?.segments?.length) return;
    setBusy(true);
    setError(null);
    try {
      // Single transactional day-level request — the backend inserts or
      // deletes every segment in one DB transaction so multi-passage days
      // are always all-or-nothing.
      const method = allCompleted ? "DELETE" : "PUT";
      const res = await fetch(
        `/api/reading-plans/${planSlug}/days/${dayDetail.date}/completion`,
        {
          method,
          headers: { ...commonHeaders, "Content-Type": "application/json" },
          credentials: "include",
          body: method === "PUT" ? JSON.stringify({ completedVia: "reader" }) : undefined,
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Could not save completion");
      }
      const payload = await res.json();
      // Optimistic in-place update — no full reload.
      setDayDetail((prev) => prev && ({
        ...prev,
        completed: payload.status === 'completed',
        segments: prev.segments.map((s) => ({
          ...s,
          completed: payload.status === 'completed',
        })),
      }));
      setProgress((prev) => ({
        ...(prev || {}),
        authenticated: true,
        completed_count: payload.completed_count,
        total_count: payload.total_count,
        percent: payload.percent,
      }));
    } catch (err) {
      setError(err.message || "Could not save completion — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const gotoDate = (target) => {
    if (target) navigate(`/bible-reading/plan/${planSlug}/${target}`);
  };

  const percentText = progress
    ? `${progress.completed_count} / ${progress.total_count} · ${progress.percent}%`
    : "—";

  // If the plan itself couldn't be loaded (missing / unpublished), show a
  // friendly full-page recovery card instead of the raw error string.
  const planMissing =
    !loading && error && /reading plan not found/i.test(error) && !dayDetail && !planMeta;
  if (planMissing) {
    return (
      <ReadingPlanUnavailable
        theme={theme}
        message="This plan isn't published yet or the link is off. Browse other plans, or head to today's devotional."
      />
    );
  }

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          {/* Plan header card */}
          <section
            className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex flex-col gap-space-md`}
          >
            {/* Mobile-only calendar toggle */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open weekly plan"
              aria-expanded={drawerOpen}
              aria-controls="reading-plan-week-drawer"
              className={`lg:hidden self-end inline-flex items-center gap-1 h-10 px-space-sm rounded-full bg-canvas-base text-neu-primary text-label-sm font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
            >
              <FiCalendar className="text-accent-cta" />
              <span>This Week</span>
            </button>
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                {planMeta?.name || "One-Year Reading Plan"}
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                {dayDetail?.segments?.length
                  ? dayDetail.segments.map((s) => s.reference_en || s.reference_ko).join(" · ")
                  : loading ? "Loading…" : "No reading"}
              </h1>
              <p className="text-body-lg text-slate-muted">
                {dayDetail?.date}
                {dayDetail?.weekday ? ` · ${dayDetail.weekday}` : ""}
                {dayDetail?.segments?.length ? ` · ${dayDetail.segments.length} passage${dayDetail.segments.length > 1 ? "s" : ""}` : ""}
              </p>
            </div>

            {/* Progress + prev/next + complete */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
              <div className="flex-1">
                <div className="flex justify-between text-label-sm text-slate-muted">
                  <span>Progress</span>
                  <span>{percentText}</span>
                </div>
                <div className={`mt-1 w-full h-3 rounded-full bg-canvas-base p-0.5 ${NEU_INSET_SM}`}>
                  <div
                    className="h-full rounded-full bg-neu-secondary transition-all"
                    style={{ width: `${progress?.percent || 0}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-space-sm">
                <button
                  type="button"
                  onClick={() => gotoDate(dayDetail?.previous_date)}
                  disabled={!dayDetail?.previous_date}
                  aria-label="Previous scheduled day"
                  className={`inline-flex items-center gap-1 h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <FiChevronLeft />
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <button
                  type="button"
                  onClick={() => gotoDate(dayDetail?.next_date)}
                  disabled={!dayDetail?.next_date}
                  aria-label="Next scheduled day"
                  className={`inline-flex items-center gap-1 h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span className="hidden sm:inline">Next</span>
                  <FiChevronRight />
                </button>
                <Link
                  to={`/reading-plans/${planSlug}`}
                  className={`inline-flex items-center gap-1 h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
                >
                  <FiCalendar className="text-accent-cta" />
                  <span className="hidden sm:inline">Full plan</span>
                </Link>
                <button
                  type="button"
                  onClick={toggleComplete}
                  disabled={busy || !dayDetail}
                  aria-pressed={allCompleted}
                  className={
                    allCompleted
                      ? `inline-flex items-center gap-2 h-11 px-space-lg rounded-full bg-canvas-base ${NEU_INSET_SM} text-accent-cta text-label-md font-semibold transition-all disabled:opacity-50`
                      : `inline-flex items-center gap-2 h-11 px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all disabled:opacity-50`
                  }
                >
                  {allCompleted ? <FiRotateCcw /> : <FiCheck />}
                  <span>
                    {busy
                      ? "Saving…"
                      : allCompleted
                        ? "Mark Incomplete"
                        : user ? "Mark Reading Complete" : "Sign in to Complete"}
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* Two-column layout: sticky week sidebar on desktop + passages */}
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-gutter items-start">
            {/* Desktop sticky sidebar */}
            <aside
              className={`hidden lg:block sticky top-6 rounded-[2rem] bg-canvas-base p-space-md ${NEU_RAISED_MD}`}
            >
              <ReadingPlanWeek
                planSlug={planSlug}
                planName={planMeta?.name}
                weekStart={weekRange.start}
                weekEnd={weekRange.end}
                daysByDate={daysByDate}
                completedIds={completedIds}
                todayISO={today?.date}
                selectedISO={date}
                loading={weekLoading}
                error={weekError}
                onSelect={(iso) => navigate(`/bible-reading/plan/${planSlug}/${iso}`)}
              />
            </aside>

            {/* Passage column */}
            <div className="flex flex-col gap-space-lg min-w-0">
              {loading && (
                <section className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-lg text-slate-muted`}>
                  Loading today’s passages…
                </section>
              )}
              {error && !loading && (
                <section className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-lg text-[#b02a37]`}>
                  {error}
                </section>
              )}

              {!loading && !error && dayDetail?.segments?.map((seg, idx) => (
                <SegmentSection
                  key={seg.id}
                  segment={seg}
                  index={idx}
                  total={dayDetail.segments.length}
                  version={version}
                  onVersionChange={changeVersion}
                  user={user}
                  onRequireLogin={openLogin}
                  language={language}
                />
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[9998] flex"
          role="dialog"
          aria-modal="true"
          aria-label="Weekly reading plan"
          id="reading-plan-week-drawer"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close weekly plan"
            onClick={() => setDrawerOpen(false)}
          />
          <div className={`relative w-[min(360px,92vw)] h-full bg-canvas-base p-space-md overflow-y-auto ${NEU_RAISED_LG} animate-slideInRight`}>
            <ReadingPlanWeek
              planSlug={planSlug}
              planName={planMeta?.name}
              weekStart={weekRange.start}
              weekEnd={weekRange.end}
              daysByDate={daysByDate}
              completedIds={completedIds}
              todayISO={today?.date}
              selectedISO={date}
              loading={weekLoading}
              error={weekError}
              onSelect={(iso) => navigate(`/bible-reading/plan/${planSlug}/${iso}`)}
              onClose={() => setDrawerOpen(false)}
              showCloseButton
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanModeReader;
